import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import {
  getNodeInstances,
  type FetchNodeInstancesParams,
  type NodeInstanceHeaderConfig,
  type NodeInstanceListRow,
} from 'api/node_instances/endpoints/getNodeInstances';
import { getNodeInstanceSubtree, type NodeInstanceSubtreeChild, type NodeInstanceSubtreeRoot } from 'api/node_instances/endpoints/getNodeInstanceSubtree';
import type { NodeInstanceChildEdge } from 'api/node_instances/endpoints/types';

export type { FetchNodeInstancesParams };

type NodeInstanceConfig = NodeInstanceHeaderConfig;

/**
 * One row of `state.nodes`. Seeded snake_case by the validated list loader (the
 * table renders `header_key` columns straight off these rows); `fetchNodeInstanceById`
 * merges in subtree-root fields plus `child_nodes`/`child_edges`. NOTE: the
 * cancel/hold/release/disable/kill/delete thunks merge RAW, unvalidated PUT
 * payloads into these rows — this is a multi-writer slice, so do not assume the
 * fields beyond the list loader's are uniformly present or validated.
 */
export interface NodeInstance extends NodeInstanceListRow {
  child_nodes?: NodeInstanceSubtreeChild[];
  child_edges?: NodeInstanceChildEdge[];
}

// Minimal recursive shape for pruning a node from a (possibly nested) tree by
// id. Both `state.nodes` rows and their `child_nodes` descendants satisfy it.
type PrunableNode = { node_instance_id: number; child_nodes?: PrunableNode[] };

interface RejectValue {
  message: string;
  error: string;
}
interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface NodeInstanceState {
  nodes: NodeInstance[];
  pagination: PaginationState;
  status: string | null;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  error: string | null;
  queue_id: number | null;
  queue_name: string | null;
  worker_id: number | null;
  worker_name: string | null;
  nodeTypes: string[];
  selectedNodeInstance: NodeInstanceSubtreeRoot[];
  config: NodeInstanceConfig[];
  filtersConfig: FilterConfig[];
  loading: {
    fetchNodeInstances: boolean;
    fetchNodeInstanceCount: boolean;
  };
  countError: 'timeout' | 'error' | null;
}

const initialState: NodeInstanceState = {
  nodes: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  search: '',
  sortBy: 'node_instance_id',
  sortOrder: 'asc',
  error: null,
  queue_id: null,
  queue_name: null,
  worker_id: null,
  worker_name: null,
  nodeTypes: [],
  selectedNodeInstance: [],
  config: [],
  filtersConfig: [],
  loading: {
    fetchNodeInstances: false,
    fetchNodeInstanceCount: false,
  },
  countError: null,
};

export const fetchNodeInstances = createAsyncThunk('nodeInstances/fetchNodeInstances', (params: FetchNodeInstancesParams) =>
  getNodeInstances(params, 'fetchNodeInstances')
);

export const fetchNodeInstanceCount = createAsyncThunk(
  'nodeInstances/fetchNodeInstanceCount',
  async (params: Record<string, any>, { signal, rejectWithValue }) => {
    try {
      const res = await api.get('/node_instances/count', {
        params: flattenQuery(params),
        signal,
        timeout: 180000,
      });
      return res.data;
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 504 || body?.timeout === true) {
        return rejectWithValue({ timeout: true });
      }
      if (status === 400 || status === 500) {
        return rejectWithValue({
          timeout: false,
          message: body?.message || body?.error || err.message,
        });
      }
      return rejectWithValue({ timeout: false, message: err.message });
    }
  }
);

// Fetch Node Types
export const fetchNodeTypes = createAsyncThunk('nodeInstances/fetchNodeTypes', async () => {
  const response = await api.get('/lists/node_types');
  return response.data.node_types;
});

export const fetchNodeInstanceById = createAsyncThunk('nodeInstances/fetchNodeInstanceById', (node_instance_id: number) =>
  getNodeInstanceSubtree(node_instance_id, 'fetchNodeInstanceById')
);

// Add a thunk for canceling a node instance
export const cancelNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/cancelNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/cancel`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const holdNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/holdNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/hold`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const releaseNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/releaseNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/release`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const disableNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/disableNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/disable`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const revertDisableNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/revertDisableNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/revert-disable`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export interface FetchNodeInstanceFileParams {
  node_instance_id: number;
  file_name: string;
}

export const fetchNodeInstanceFile = createAsyncThunk<any, FetchNodeInstanceFileParams, { rejectValue: string }>(
  'nodeInstances/fetchNodeInstanceFile',
  async ({ node_instance_id, file_name }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/node_instances/${node_instance_id}/files/${file_name}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const killNodeInstance = createAsyncThunk<any, { node_instance_id: number }, { rejectValue: string }>(
  'nodeInstances/killNodeInstance',
  async ({ node_instance_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/node_instances/${node_instance_id}/kill`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const deleteNodeInstance = createAsyncThunk<
  { node_instance_id: number },
  { node_instance_id: number; skip_worker_deletion?: boolean },
  { rejectValue: string }
>('nodeInstances/deleteNodeInstance', async ({ node_instance_id, skip_worker_deletion = false }, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/node_instances/${node_instance_id}`, {
      data: { skip_worker_deletion },
    });
    // backend always returns { message, node_instance_id }
    return { node_instance_id };
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
  }
});

export const deleteMultipleNodeInstances = createAsyncThunk<
  { deleted_ids: number[] },
  { node_instance_ids: number[]; skip_worker_deletion?: boolean },
  { rejectValue: string }
>('nodeInstances/deleteMultipleNodeInstances', async ({ node_instance_ids, skip_worker_deletion = false }, { rejectWithValue }) => {
  try {
    const deletePromises = node_instance_ids.map((id) =>
      api.delete(`/node_instances/${id}`, {
        data: { skip_worker_deletion },
      })
    );

    await Promise.all(deletePromises);
    return { deleted_ids: node_instance_ids };
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
  }
});

const updateNodeEverywhere = (nodes, updatedNode) => {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    // Replace matching node_instance_id
    if (node.node_instance_id === updatedNode.node_instance_id) {
      nodes[i] = {
        ...node,
        ...updatedNode,
        // do not overwrite existing children if update doesn't include them
        child_nodes: updatedNode.child_nodes ?? node.child_nodes,
        child_edges: updatedNode.child_edges ?? node.child_edges,
      };
    }

    // Recursively update children
    if (node.child_nodes?.length) {
      updateNodeEverywhere(node.child_nodes, updatedNode);
    }
  }
};

const nodeInstancesSlice = createSlice({
  name: 'nodeInstances',
  initialState,
  reducers: {
    setSortOrder: (state, action) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNodeInstances.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchNodeInstances = true;
      })
      .addCase(fetchNodeInstances.fulfilled, (state, action) => {
        const { pagination, nodes, config, filtersConfig } = action.payload;
        state.nodes = nodes;
        state.config = config;
        state.filtersConfig = filtersConfig || [];
        // List no longer returns total_records/total_pages; they come from count endpoint
        state.pagination.pageSize = pagination.limit;
        // Only clamp to page 1 when we have a known total and server returned an out-of-range page.
        // While count is still loading (totalPages 0), trust the list response's current_page.
        state.pagination.currentPage = state.pagination.totalPages > 0 && pagination.current_page > state.pagination.totalPages ? 1 : pagination.current_page;
        if (pagination.total_records != null) {
          state.pagination.totalItems = pagination.total_records;
        }
        if (pagination.total_pages != null) {
          state.pagination.totalPages = pagination.total_pages;
        }

        state.status = 'succeeded';
        state.loading.fetchNodeInstances = false;
      })
      .addCase(fetchNodeInstances.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'An error occurred';
        state.loading.fetchNodeInstances = false;
      })
      .addCase(fetchNodeInstanceCount.pending, (state) => {
        state.loading.fetchNodeInstanceCount = true;
        state.countError = null;
      })
      .addCase(fetchNodeInstanceCount.fulfilled, (state, action) => {
        const { total_records, total_pages, limit } = action.payload;
        state.pagination.totalItems = total_records ?? 0;
        state.pagination.totalPages = total_pages ?? 0;
        if (limit != null) {
          state.pagination.pageSize = limit;
        }
        state.loading.fetchNodeInstanceCount = false;
        state.countError = null;
      })
      .addCase(fetchNodeInstanceCount.rejected, (state, action) => {
        if (action.error.name === 'AbortError' || action.error.name === 'CanceledError') {
          return;
        }
        const payload = action.payload as { timeout?: boolean; message?: string } | undefined;
        state.loading.fetchNodeInstanceCount = false;
        state.countError = payload?.timeout === true ? 'timeout' : 'error';
        if (payload?.message && !payload?.timeout) {
          state.error = payload.message;
        }
      })
      .addCase(fetchNodeTypes.fulfilled, (state, action) => {
        state.nodeTypes = action.payload;
      })
      .addCase(fetchNodeTypes.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch node types';
      })
      .addCase(fetchNodeInstanceById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.selectedNodeInstance = [];
      })
      .addCase(fetchNodeInstanceById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const node = action.payload;

        updateNodeEverywhere(state.nodes, node);

        const sortByXRecursively = (nodes = []) => {
          return nodes
            .slice()
            .sort((a, b) => (a?.position?.x ?? 0) - (b?.position?.x ?? 0))
            .map((n) => ({
              ...n,
              child_nodes: n.child_nodes ? sortByXRecursively(n.child_nodes) : [],
            }));
        };
        state.nodes = sortByXRecursively(state.nodes);

        state.selectedNodeInstance.push(node);
      })
      .addCase(fetchNodeInstanceById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch node instance';
        state.selectedNodeInstance = [];
      })
      // Add the cancelNodeInstance cases
      .addCase(cancelNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(cancelNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { updated_node_instance } = action.payload;
        const { node_instance_id, ...updatedData } = updated_node_instance;

        const index = state.nodes.findIndex((node) => {
          return node.node_instance_id === node_instance_id;
        });

        if (index !== -1) {
          state.nodes[index] = {
            ...state.nodes[index],
            ...updatedData,
          };
          const toastId = 'data-succedeed-to-cancel-node-instance';
          if (!toast.isActive(toastId)) {
            toast.success(`Successfully cancelled node instance!`, { toastId });
          }
        } else {
          console.warn(`Node instance with id ${node_instance_id} not found in state`);
        }
      })
      .addCase(cancelNodeInstance.rejected, (state, action) => {
        state.error = action.payload || 'Failed to cancel node instance';
        console.log('ddd', action);
        const toastId = 'data-failed-to-cancel-node-instance';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to cancel node instance! ${state.error}`, {
            toastId,
          });
        }
      })

      // Add the holdNodeInstance cases
      .addCase(holdNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(holdNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { updated_node_instance } = action.payload;
        const { node_instance_id, ...updatedData } = updated_node_instance;

        const index = state.nodes.findIndex((node) => {
          return node.node_instance_id === node_instance_id;
        });

        if (index !== -1) {
          state.nodes[index] = {
            ...state.nodes[index],
            ...updatedData,
          };
          const toastId = 'data-succedeed-to-hold-node-instance';
          if (!toast.isActive(toastId)) {
            toast.success(`Successfully held node instance!`, { toastId });
          }
        } else {
          console.warn(`Node instance with id ${node_instance_id} not found in state`);
        }
      })
      .addCase(holdNodeInstance.rejected, (state, action) => {
        state.error = action.payload || 'Failed to hold node instance';
        console.log('ddd', action);
        const toastId = 'data-failed-to-hold-node-instance';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to hold node instance! ${state.error}`, {
            toastId,
          });
        }
      })

      // Add the releaseNodeInstance cases
      .addCase(releaseNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(releaseNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { updated_node_instance } = action.payload;
        const { node_instance_id, ...updatedData } = updated_node_instance;

        const index = state.nodes.findIndex((node) => {
          return node.node_instance_id === node_instance_id;
        });

        if (index !== -1) {
          state.nodes[index] = {
            ...state.nodes[index],
            ...updatedData,
          };
          const toastId = 'data-succedeed-to-release-node-instance';
          if (!toast.isActive(toastId)) {
            toast.success(`Successfully released node instance!`, { toastId });
          }
        } else {
          console.warn(`Node instance with id ${node_instance_id} not found in state`);
        }
      })
      .addCase(releaseNodeInstance.rejected, (state, action) => {
        state.error = action.payload || 'Failed to release node instance';
        console.log('ddd', action);
        const toastId = 'data-failed-to-release-node-instance';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to release node instance! ${state.error}`, {
            toastId,
          });
        }
      })
      .addCase(disableNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(disableNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { updated_node_instance } = action.payload;

        updateNodeEverywhere(state.nodes, updated_node_instance);

        const toastId = 'data-disabled-node-instance';
        if (!toast.isActive(toastId)) {
          toast.success('Node instance disabled successfully', {
            toastId,
          });
        }
      })
      .addCase(disableNodeInstance.rejected, (state, action) => {
        state.error = action.payload || 'Failed to disable node instance';
        const toastId = 'data-failed-to-disable-node-instance';

        if (!toast.isActive(toastId)) {
          toast.error(action.payload || 'Failed to disable node instance', {
            toastId,
          });
        }
      })
      .addCase(revertDisableNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(revertDisableNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { updated_node_instance } = action.payload;

        updateNodeEverywhere(state.nodes, updated_node_instance);

        const toastId = 'data-reverted-disable-node-instance';

        if (!toast.isActive(toastId)) {
          toast.success('Node instance reverted to scheduled successfully', {
            toastId,
          });
        }
      })
      .addCase(revertDisableNodeInstance.rejected, (state, action) => {
        state.status = action.payload || 'Failed to revert disabled node instance';
        const toastId = 'data-failed-to-revert-disable-node-instance';
        if (!toast.isActive(toastId)) {
          toast.error(action.payload || 'Failed to revert disabled node instance', { toastId });
        }
      })
      // Add the fetchNodeInstanceFile cases
      .addCase(fetchNodeInstanceFile.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNodeInstanceFile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Handle the file data as needed
        // const toastId = "data-succeeded-to-fetch-node-instance-file";
        // if (!toast.isActive(toastId)) {
        //   toast.success('Successfully fetched node instance file!', { toastId });
        // }
      })
      .addCase(fetchNodeInstanceFile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch node instance file';
        // const toastId = "data-failed-to-fetch-node-instance-file";
        // if (!toast.isActive(toastId)) {
        //   toast.error(state.error, { toastId });
        // }
      })
      // DELETE NODE INSTANCE
      .addCase(deleteNodeInstance.fulfilled, (state, action) => {
        const { node_instance_id } = action.payload;

        // Remove from root list
        state.nodes = state.nodes.filter((n) => n.node_instance_id !== node_instance_id);

        // Also remove from any nested child structure (tree mode)
        const removeFromChildren = (nodes: PrunableNode[]) => {
          for (const node of nodes) {
            if (node.child_nodes) {
              node.child_nodes = node.child_nodes.filter((child) => child.node_instance_id !== node_instance_id);
              removeFromChildren(node.child_nodes);
            }
          }
        };
        removeFromChildren(state.nodes);

        const toastId = 'node-instance-delete-success';
        if (!toast.isActive(toastId)) {
          toast.success(`Node instance ${node_instance_id} deleted.`, {
            toastId,
          });
        }
      })

      .addCase(deleteNodeInstance.rejected, (state, action) => {
        const msg = action.payload || 'Failed to delete node instance';
        state.error = msg;

        // detect 409 (active instance cannot delete)
        if (msg.includes('Cannot delete') || msg.includes('active')) {
          toast.warning(msg);
        } else {
          toast.error(msg);
        }
      })

      .addCase(deleteMultipleNodeInstances.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteMultipleNodeInstances.fulfilled, (state, action) => {
        const { deleted_ids } = action.payload;

        state.nodes = state.nodes.filter((n) => !deleted_ids.includes(n.node_instance_id));

        const removeFromChildren = (nodes: PrunableNode[]) => {
          for (const node of nodes) {
            if (node.child_nodes) {
              node.child_nodes = node.child_nodes.filter((child) => !deleted_ids.includes(child.node_instance_id));
              removeFromChildren(node.child_nodes);
            }
          }
        };
        removeFromChildren(state.nodes);

        const toastId = 'node-instances-bulk-delete-success';
        if (!toast.isActive(toastId)) {
          toast.success(`Successfully deleted ${deleted_ids.length} node instance(s).`, { toastId });
        }
      })
      .addCase(deleteMultipleNodeInstances.rejected, (state, action) => {
        const msg = action.payload || 'Failed to delete node instances';
        state.error = msg;
        state.status = 'failed';

        const toastId = 'node-instances-bulk-delete-error';
        if (!toast.isActive(toastId)) {
          toast.error(msg, { toastId });
        }
      })

      .addCase(killNodeInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(killNodeInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const nodeId = action.meta && action.meta.arg ? action.meta.arg.node_instance_id : undefined;

        if (nodeId) {
          const idx = state.nodes.findIndex((n) => n.node_instance_id === nodeId);
          if (idx !== -1) {
            state.nodes[idx] = { ...state.nodes[idx], status: 'killing' };
          }
        }

        const toastId = 'data-succeeded-to-kill-node-instance';
        if (!toast.isActive(toastId)) {
          toast.success('Kill requested. Node is moving to "killing".', {
            toastId,
          });
        }
      })
      .addCase(killNodeInstance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to kill node instance';
        const toastId = 'data-failed-to-kill-node-instance';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to kill node instance! ${state.error}`, {
            toastId,
          });
        }
      });
  },
});

export const { setSortOrder, setSearch } = nodeInstancesSlice.actions;

export default nodeInstancesSlice.reducer;
